/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { FormGroup, FormLabel } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import { Modal } from "components/molecules/Modal.molecule";
import { FormButton } from "components-old/modal-elems";
import SelectField from "components-old/forms/fields/SelectField";
import Colors from "styles/colors";
import { ReportDTO } from "pages/reports/bi-dashboard-next/models/ReportDTO";
import { EmailScheduleDTO } from "pages/reports/bi-dashboard-next/hooks/useEmailSchedule";

interface SelectOption {
  label: string;
  value: string;
  creatorEmail?: string;
}

interface SelectScheduleModalProps {
  show: boolean;
  hide: () => void;
  report: ReportDTO;
  schedules: EmailScheduleDTO[];
  isLoading?: boolean;
  showCreateScheduleModal: () => void;
  showUpdateScheduleModal: (schedule: EmailScheduleDTO) => void;
  hasManageSharedReportsPrivilege?: boolean;
}

export const SelectScheduleModal: React.FC<SelectScheduleModalProps> = ({
  show,
  hide,
  report,
  schedules = [],
  isLoading = false,
  showCreateScheduleModal,
  showUpdateScheduleModal,
  hasManageSharedReportsPrivilege = false,
}) => {
  const { t } = useTranslation("reports");

  const [scheduleName, setScheduleName] = useState<string | undefined>(
    undefined,
  );
  const [creatorEmail, setCreatorEmail] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (show === false) {
      setScheduleName(undefined);
      setCreatorEmail(undefined);
    } else if (schedules.length > 0) {
      const isMyReport = report.privateUserId !== "";
      if (isMyReport) {
        setCreatorEmail(schedules[0]?.creatorActorId);
      }
    }
  }, [show, schedules, report]);

  let nameOptions: SelectOption[] = schedules.map((sched) => ({
    label: sched.name,
    creatorEmail: sched.creatorActorId,
    value: sched.name,
  }));

  if (hasManageSharedReportsPrivilege && creatorEmail) {
    nameOptions = nameOptions.filter((option) => {
      return option.creatorEmail === creatorEmail;
    });
  }

  const onNameChange = (option: SelectOption) => {
    setScheduleName(option.value);
  };

  const creatorEmailOptions: SelectOption[] = _.uniqBy(
    schedules.map((sched) => ({
      label: sched.creatorActorId || "",
      value: sched.creatorActorId || "",
    })),
    "value",
  );

  const onCreatorEmailChange = (option: SelectOption) => {
    setCreatorEmail(option.value);
    setScheduleName(undefined);
  };

  let isScheduleNameSelectDisabled = false;
  if (hasManageSharedReportsPrivilege) {
    isScheduleNameSelectDisabled = _.isNil(creatorEmail);
  }

  let isOpenDisabled = _.isNil(scheduleName);
  if (hasManageSharedReportsPrivilege) {
    isOpenDisabled = _.isNil(creatorEmail) || isOpenDisabled;
  }

  const handleOpenSchedule = () => {
    if (scheduleName) {
      const schedule = schedules.find((sched) => sched.name === scheduleName);
      if (schedule) {
        showUpdateScheduleModal(schedule);
      }
    }
  };

  const showOpenScheduleButton = schedules.length > 0;

  return (
    <Modal backdrop="static" show={show} onHide={() => hide()} {...({} as any)}>
      <Modal.Header
        title={t("reports:Select Schedule")}
        isLoading={isLoading}
      />
      <Modal.Body {...({} as any)}>
        {schedules.length === 0 ? (
          <div css={{ textAlign: "center", padding: "20px 0" }}>
            <p>{t("reports:No schedules exist for this report yet.")}</p>
            <p>
              {t(
                "reports:Click 'Create Schedule' to create your first schedule.",
              )}
            </p>
          </div>
        ) : (
          <>
            {hasManageSharedReportsPrivilege &&
              creatorEmailOptions.length > 0 && (
                <FormGroup css={{ marginBottom: "1rem" }}>
                  <FormLabel className="fw-bold">
                    {t("reports:Creator Email")}:
                  </FormLabel>
                  {React.createElement(SelectField as any, {
                    stateValue: creatorEmail,
                    options: creatorEmailOptions,
                    onChange: onCreatorEmailChange,
                    isSearchable: false,
                  })}
                </FormGroup>
              )}
            <FormGroup css={{ marginBottom: "1rem" }}>
              <FormLabel className="fw-bold">
                {t("reports:Schedule Name")}:
              </FormLabel>
              {React.createElement(SelectField as any, {
                stateValue: scheduleName,
                options: nameOptions,
                onChange: onNameChange,
                isSearchable: false,
                isDisabled: isScheduleNameSelectDisabled,
              })}
            </FormGroup>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <FormButton
          label={t("reports:Create Schedule")}
          style={{
            marginRight: "auto",
            background: Colors.background.LIGHTER_BLUE,
            color: "white",
          }}
          clickHandler={() => showCreateScheduleModal()}
        />
        {showOpenScheduleButton && (
          <FormButton
            actionType={null}
            label={t("reports:Open Schedule")}
            disabled={isOpenDisabled}
            clickHandler={handleOpenSchedule}
          />
        )}
        <FormButton label={t("reports:Cancel")} clickHandler={() => hide()} />
      </Modal.Footer>
    </Modal>
  );
};
